import React, { useState, useContext, useMemo } from 'react';
import { AppContext } from '../contexts/AppContext';
import { useUI } from '../contexts/UIContext';
import { Rol, Usuario } from '../types';
import Modal from '../components/Modal';

type StaffRol = 'Cajero' | 'Viewer';

const UsuariosScreen: React.FC = () => {
    const { user, usuarios, createStaffUser, updateStaffUser, deleteStaffUser, isReadOnly } = useContext(AppContext);
    const ui = useUI();

    const [filtro, setFiltro] = useState<'todos' | StaffRol | 'Admin'>('todos');
    const [busqueda, setBusqueda] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editing, setEditing] = useState<Usuario | null>(null);
    const [form, setForm] = useState<{ nombre: string; email: string; rol: StaffRol }>({ nombre: '', email: '', rol: 'Cajero' });
    const [saving, setSaving] = useState(false);
    const [toDelete, setToDelete] = useState<Usuario | null>(null);
    const [deleting, setDeleting] = useState(false);

    // Si no es Admin → mensaje (la pantalla no debería estar accesible vía menú, pero por las dudas).
    if (user?.rol !== Rol.ADMIN) {
        return (
            <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Usuarios</h2>
                <p className="text-gray-600">Solo el Administrador del club puede gestionar usuarios.</p>
            </div>
        );
    }

    const visibles = useMemo(() => {
        let arr = (usuarios || []);
        if (filtro !== 'todos') arr = arr.filter(u => u.rol === filtro);
        const q = busqueda.trim().toLowerCase();
        if (q) arr = arr.filter(u => (u.nombre || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q));
        // Admin primero, después por nombre.
        return arr.slice().sort((a, b) => {
            if (a.rol === 'Admin' && b.rol !== 'Admin') return -1;
            if (b.rol === 'Admin' && a.rol !== 'Admin') return 1;
            return (a.nombre || '').localeCompare(b.nombre || '');
        });
    }, [usuarios, filtro, busqueda]);

    const openCreate = () => {
        setEditing(null);
        setForm({ nombre: '', email: '', rol: 'Cajero' });
        setIsModalOpen(true);
    };

    const openEdit = (u: Usuario) => {
        if (u.rol === 'Admin' || u.rol === 'SuperAdmin') return; // no editamos admins
        setEditing(u);
        setForm({ nombre: u.nombre || '', email: u.email || '', rol: (u.rol as StaffRol) });
        setIsModalOpen(true);
    };

    const closeModal = () => {
        if (saving) return;
        setIsModalOpen(false);
        setEditing(null);
    };

    const handleSave = async () => {
        const nombre = form.nombre.trim();
        if (!nombre) { ui.showModal('Error', 'El nombre es obligatorio.'); return; }

        setSaving(true);
        try {
            if (editing) {
                const res = await updateStaffUser(editing.id, { nombre, rol: form.rol });
                if (!res.success) { ui.showModal('Error', res.message || 'No se pudo actualizar.'); return; }
                ui.showToast('success', 'Usuario actualizado.');
            } else {
                const email = form.email.trim().toLowerCase();
                if (!email) { ui.showModal('Error', 'El email es obligatorio.'); return; }
                if (!/^\S+@\S+\.\S+$/.test(email)) { ui.showModal('Error', 'Email inválido.'); return; }
                const res = await createStaffUser({ nombre, email, rol: form.rol });
                if (!res.success) { ui.showModal('Error', res.message || 'No se pudo crear el usuario.'); return; }
                ui.showToast('success', 'Usuario creado. Se envió un enlace de acceso a su email.');
            }
            closeModal();
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        if (!toDelete) return;
        setDeleting(true);
        const res = await deleteStaffUser(toDelete.id);
        setDeleting(false);
        if (!res.success) {
            ui.showModal('Error', res.message || 'No se pudo eliminar.');
            return;
        }
        ui.showToast('success', 'Usuario eliminado.');
        setToDelete(null);
    };

    return (
        <div className="bg-white rounded-lg shadow-md">
            <div className="p-4 md:p-6 border-b">
                <div className="flex flex-wrap gap-4 justify-between items-center">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800">Usuarios del club</h2>
                        <p className="text-sm text-gray-500">Administradores, cajeros y usuarios de solo lectura.</p>
                    </div>
                    <button
                        onClick={openCreate}
                        disabled={isReadOnly}
                        className="bg-blue-600 text-white px-5 py-2 rounded-md hover:bg-blue-700 font-semibold disabled:bg-gray-400"
                    >
                        + Nuevo usuario
                    </button>
                </div>
                <div className="flex flex-wrap gap-3 mt-4">
                    <input
                        type="text"
                        placeholder="Buscar por nombre o email..."
                        value={busqueda}
                        onChange={e => setBusqueda(e.target.value)}
                        className="flex-grow sm:flex-grow-0 sm:w-72 p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="flex gap-1">
                        {(['todos', 'Admin', 'Cajero', 'Viewer'] as const).map(f => (
                            <button
                                key={f}
                                onClick={() => setFiltro(f)}
                                className={`px-3 py-2 text-sm rounded border capitalize ${filtro === f ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rol</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {visibles.map(u => {
                            const esYo = u.id === user?.id;
                            const esAdmin = u.rol === 'Admin' || u.rol === 'SuperAdmin';
                            return (
                                <tr key={u.id}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-gray-900">{u.nombre}</span>
                                            {esYo && <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">vos</span>}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{u.email || '—'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${u.rol === 'Admin' ? 'bg-purple-100 text-purple-800' : u.rol === 'Cajero' ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`}>
                                            {u.rol}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-4">
                                        {!esAdmin && !esYo && (
                                            <>
                                                <button onClick={() => openEdit(u)} className="text-yellow-600 hover:text-yellow-900">Editar</button>
                                                <button onClick={() => setToDelete(u)} className="text-red-600 hover:text-red-900">Eliminar</button>
                                            </>
                                        )}
                                        {esAdmin && <span className="text-xs text-gray-400">—</span>}
                                    </td>
                                </tr>
                            );
                        })}
                        {visibles.length === 0 && (
                            <tr><td colSpan={4} className="px-6 py-8 text-center text-gray-500">No hay usuarios para mostrar.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-gray-200">
                {visibles.map(u => {
                    const esYo = u.id === user?.id;
                    const esAdmin = u.rol === 'Admin' || u.rol === 'SuperAdmin';
                    return (
                        <div key={u.id} className="p-4 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                                <div className="font-semibold text-gray-800 truncate">{u.nombre} {esYo && <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full ml-1">vos</span>}</div>
                                <div className="text-xs text-gray-500 truncate">{u.email || '—'}</div>
                                <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-semibold rounded-full ${u.rol === 'Admin' ? 'bg-purple-100 text-purple-800' : u.rol === 'Cajero' ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`}>{u.rol}</span>
                            </div>
                            {!esAdmin && !esYo && (
                                <div className="flex flex-col gap-1 text-sm">
                                    <button onClick={() => openEdit(u)} className="text-yellow-600">Editar</button>
                                    <button onClick={() => setToDelete(u)} className="text-red-600">Eliminar</button>
                                </div>
                            )}
                        </div>
                    );
                })}
                {visibles.length === 0 && <div className="p-6 text-center text-gray-500">No hay usuarios para mostrar.</div>}
            </div>

            {/* Modal crear/editar */}
            {isModalOpen && (
                <Modal isOpen={isModalOpen} onClose={closeModal} title={editing ? 'Editar usuario' : 'Nuevo usuario'}>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                            <input
                                type="text"
                                value={form.nombre}
                                onChange={e => setForm(prev => ({ ...prev, nombre: e.target.value }))}
                                className="w-full p-2 border rounded"
                                disabled={saving}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email {editing && <span className="text-xs text-gray-500 font-normal">(no editable)</span>}</label>
                            <input
                                type="email"
                                value={form.email}
                                onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
                                className="w-full p-2 border rounded disabled:bg-gray-100"
                                disabled={saving || !!editing}
                                placeholder="empleado@email.com"
                            />
                            {!editing && (
                                <p className="text-xs text-gray-500 mt-1">Se le enviará automáticamente un enlace de acceso a este email.</p>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
                            <select
                                value={form.rol}
                                onChange={e => setForm(prev => ({ ...prev, rol: e.target.value as StaffRol }))}
                                className="w-full p-2 border rounded"
                                disabled={saving}
                            >
                                <option value="Cajero">Cajero — opera caja, turnos y socios</option>
                                <option value="Viewer">Viewer — solo lectura</option>
                            </select>
                        </div>
                        <div className="flex justify-end gap-3 pt-2">
                            <button onClick={closeModal} disabled={saving} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 font-medium">Cancelar</button>
                            <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium disabled:bg-gray-400">
                                {saving ? 'Guardando...' : (editing ? 'Guardar cambios' : 'Crear y enviar enlace')}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Modal eliminar */}
            {toDelete && (
                <Modal isOpen={!!toDelete} onClose={() => !deleting && setToDelete(null)} title="Eliminar usuario">
                    <div className="space-y-4">
                        <p className="text-gray-700">¿Eliminar a <span className="font-bold">{toDelete.nombre}</span> ({toDelete.email})?</p>
                        <p className="text-sm text-gray-600">Pierde acceso inmediatamente. La cuenta de email queda en el sistema (para audit trail) pero sin perfil del club.</p>
                        <div className="flex justify-end gap-3 pt-2">
                            <button onClick={() => setToDelete(null)} disabled={deleting} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 font-medium">Cancelar</button>
                            <button onClick={confirmDelete} disabled={deleting} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium disabled:bg-gray-400">
                                {deleting ? 'Eliminando...' : 'Eliminar'}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default UsuariosScreen;
